import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import Markdown from "react-markdown";

import { fetchDocument, type DocumentRef } from "../api";

/**
 * One stored artifact, fetched on demand.
 *
 * **`rehype-raw` is not used, and an oxlint rule forbids importing it.** This
 * renders LLM-authored markdown and excerpts of a third party's source;
 * `react-markdown` escapes HTML by default, and turning that off is the one
 * change that would make the content executable.
 *
 * Diffs get a hand-rolled renderer rather than a library — three line
 * prefixes and a `<span>` each. It is deliberately not `innerHTML`, which is
 * where a diff renderer usually ends up.
 */
/** What each stored artifact is, in words rather than a column value. */
const KIND_LABEL: Record<string, string> = {
  diagnosis: "Diagnosis",
  diff: "Diff",
  reproduction_test: "Reproduction test",
  mutation_result: "Mutation check",
  triage_report: "Triage report",
  fix_plan: "Fix plan",
};

export function Artifact({ document }: { document: DocumentRef }) {
  const [open, setOpen] = useState(false);
  const body = useQuery({
    // Primitives only in the key: an object key re-serialises on every render
    // and misses the cache it was meant to hit.
    queryKey: ["document", document.id],
    queryFn: () => fetchDocument(document.id, document.size_bytes),
    enabled: open,
    staleTime: Number.POSITIVE_INFINITY,
  });

  return (
    <details className="artifact" open={open}>
      <summary
        onClick={(event) => {
          event.preventDefault();
          setOpen(!open);
        }}
      >
        <code>{KIND_LABEL[document.kind] ?? document.kind}</code>{" "}
        <span className="muted">
          {document.agent} · {formatBytes(document.size_bytes)}
        </span>
      </summary>

      {open ? (
        body.isPending ? (
          <p className="muted">Loading…</p>
        ) : body.error ? (
          <p className="error">{body.error.message}</p>
        ) : document.kind === "diff" ? (
          <Diff text={body.data} />
        ) : document.kind === "reproduction_test" ? (
          // Java, not markdown. Rendering it through the markdown pipeline
          // would swallow the indentation that makes it readable.
          <pre className="diff">{body.data}</pre>
        ) : (
          <div className="markdown">
            <Markdown>{body.data}</Markdown>
          </div>
        )
      ) : null}
    </details>
  );
}

function Diff({ text }: { text: string }) {
  return (
    <pre className="diff">
      {text.split("\n").map((line, index) => (
        // eslint-disable-next-line react/no-array-index-key -- lines have no id
        <span key={index} className={diffClass(line)}>
          {line}
          {"\n"}
        </span>
      ))}
    </pre>
  );
}

function diffClass(line: string): string {
  if (line.startsWith("+++") || line.startsWith("---")) return "diff-file";
  if (line.startsWith("@@")) return "diff-hunk";
  if (line.startsWith("+")) return "diff-add";
  if (line.startsWith("-")) return "diff-del";
  return "";
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
